import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import * as React from 'react'
import type mapboxgl from 'mapbox-gl'
import usePrevious from 'hooks/usePrevious'
import SearchMapMarker from './Markers/SearchMapMarker'
import { getMarkersByOffers, decodeOfferIds } from './utils'
import { getPadding, useVisitedMarkersState } from '../../Search/SearchResultsMap/utils'
import debounce from 'lodash.debounce'
import TextButton from 'LuxKit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import ExperienceSearchMapCarousel from './ExperienceSearchMapCarousel'
import useSelectedItems from '../hooks/useSelectedItems'
import useScreenSize from 'hooks/useScreenSize'
import { unique } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'
import { MapboxMap } from 'components/Common/Mapbox/MapboxMap'
import { MapboxProvider } from 'contexts/MapboxContext'
import { MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import type { MapBounds } from 'lib/maps/types'
import { mapBoundsToMapboxBounds, parseMapBounds } from 'lib/maps/bounds'
import {
  centerAndZoomOnMarker,
  fitBoundsOnMap,
  fitMarkersOnMap,
  isMarkerWithinBounds,
} from 'lib/maps/mapboxSearchMap'
import { getSearchBoundsForMap } from 'components/Search/SearchResultsMap/searchBounds'

const MapWrapper = styled.div`
  height: 100%;
  position: relative;
  overflow: hidden;
`

const FloatingBanner = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const MARKER_DISPLAY_TIMEOUT = 2000

interface Props {
  windowSearch: string
  filters: App.OfferListFilters
  experiences: Array<App.ExperienceOffer>
  fetching: boolean
  onBoundsChanged?: (newBounds: MapBounds) => void
  rootRef?: React.RefObject<HTMLDivElement | null>
  hideOfferCarousel?: boolean
}

function ExperienceSearchMap(props: Props) {
  const intl = useIntl()
  const { windowSearch, filters, fetching, experiences, onBoundsChanged, rootRef, hideOfferCarousel } = props

  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const ignoreNextMoveEnd = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const { setSelectedItems } = useSelectedItems()
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()
  const [hoveredOffer, setHoveredOffer] = useState<{ id: string, type: string } | undefined>(undefined)
  const [recentSearchResults, setRecentSearchResults] = useState<Array<Array<App.ExperienceOffer>>>([])
  const [markers, setMarkers] = useState<Array<App.SearchExperienceMapMarker>>([])
  const [markerDisplayTimedOut, setMarkerDisplayTimedOut] = useState(false)
  const [waitingForOffersAfterFilterChange, setWaitingForOffersAfterFilterChange] = useState(false)
  const [lastSearch, setLastSearch] = useState({
    destination: false,
    bounds: false,
  })
  const [hasInitialCameraApplied, setHasInitialCameraApplied] = useState(false)

  const prevFilters = usePrevious(filters)
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters?.destinationId
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters?.bounds

  const isMobileSize = useScreenSize() === 'mobile'

  useEffect(() => {
    if (isNewDestination || isNewBounds) {
      setLastSearch({
        destination: isNewDestination,
        bounds: isNewBounds,
      })
    }
  }, [isNewBounds, isNewDestination])

  useEffect(() => {
    setMarkerDisplayTimedOut(false)
    const timeoutId = setTimeout(() => {
      setMarkerDisplayTimedOut(true)
    }, MARKER_DISPLAY_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [filters.destinationId, filters.bounds])

  useEffect(() => {
    if (!fetching) {
      setRecentSearchResults([experiences])
    }
  }, [fetching, experiences])

  useEffect(() => {
    const filtersChanged = prevFilters?.destinationId !== filters.destinationId ||
      JSON.stringify(prevFilters?.categories) !== JSON.stringify(filters.categories) ||
      prevFilters?.priceLte !== filters.priceLte ||
      prevFilters?.priceGte !== filters.priceGte

    if (filtersChanged) {
      setWaitingForOffersAfterFilterChange(true)
    }
  }, [filters.destinationId, filters.categories, filters.priceLte, filters.priceGte, prevFilters])

  useEffect(() => {
    if (markerDisplayTimedOut) {
      if (waitingForOffersAfterFilterChange && experiences.length > 0) {
        setRecentSearchResults([experiences])
        setWaitingForOffersAfterFilterChange(false)
        return
      }

      // Only combine offers if we're not waiting for offers after a filter change
      if (!waitingForOffersAfterFilterChange) {
        const currentRecentSearchResults =
          recentSearchResults.length > 0 ? recentSearchResults[0] : []
        const nextRecentSearchResults = experiences
        const combinedRecentSearchResults =
          unique([...nextRecentSearchResults, ...currentRecentSearchResults])

        setRecentSearchResults([combinedRecentSearchResults])
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experiences, markerDisplayTimedOut, waitingForOffersAfterFilterChange])

  const currentOffers = useMemo(() => {
    return recentSearchResults.flat()
  }, [recentSearchResults])

  const currentBounds = useMemo(() => {
    return filters.bounds !== undefined ? parseMapBounds(filters.bounds) : undefined
  }, [filters.bounds])

  const currentMarkers = useMemo(() => {
    const offerMarkers = getMarkersByOffers(currentOffers)
    const markerResult = currentBounds
      ? offerMarkers.filter((marker) => isMarkerWithinBounds(marker, currentBounds))
      : offerMarkers
    return markerResult
  }, [currentOffers, currentBounds])

  useEffect(() => {
    setMarkers(currentMarkers)
  }, [currentMarkers])

  const params = parseSearchString(windowSearch)

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds)
  ), [params.selectedOfferIds])
  const onMarkerClick = useCallback((marker: App.SearchExperienceMapMarker) => {
    if (marker.experienceItems) {
      setSelectedItems(marker.experienceItems)
    }
  }, [setSelectedItems])

  const onDeselectOffers = useCallback(() => {
    setSelectedItems([])
  }, [setSelectedItems])

  const focusMap = useCallback((nextMap: mapboxgl.Map) => {
    ignoreNextMoveEnd.current = true

    if (filters.bounds !== undefined) {
      fitBoundsOnMap(
        nextMap,
        mapBoundsToMapboxBounds(parseMapBounds(filters.bounds)),
        { padding: getPadding(true), duration: 0 },
        0,
      )
      return true
    }

    if (selectedOfferIds.length > 0 && currentMarkers.length > 0) {
      const selectedMarker = currentMarkers.find((marker) => marker.id.split(',').includes(selectedOfferIds[0].id))
      if (selectedMarker) {
        centerAndZoomOnMarker(nextMap, selectedMarker, true)
        return true
      }
    }

    if (currentMarkers.length > 0) {
      fitMarkersOnMap(nextMap, currentMarkers, getPadding(true))
      return true
    }

    return false
  }, [currentMarkers, filters.bounds, selectedOfferIds])

  const handleMapReady = useCallback((nextMap: mapboxgl.Map) => {
    setMap(nextMap)
    if (focusMap(nextMap)) {
      setHasInitialCameraApplied(true)
    }
  }, [focusMap])

  useEffect(() => {
    if (!hasInitialCameraApplied && map && focusMap(map)) {
      setHasInitialCameraApplied(true)
    }
  }, [focusMap, hasInitialCameraApplied, map])

  useEffect(() => {
    if (map && currentMarkers.length > 0 && lastSearch.destination && !lastSearch.bounds) {
      ignoreNextMoveEnd.current = true
      fitMarkersOnMap(map, currentMarkers, getPadding(true))
    }
  }, [currentMarkers, lastSearch.bounds, lastSearch.destination, map])

  useEffect(() => {
    if (prevFilters && (
      prevFilters.destinationId !== filters.destinationId ||
      JSON.stringify(prevFilters.categories) !== JSON.stringify(filters.categories) ||
      prevFilters.priceLte !== filters.priceLte ||
      prevFilters.priceGte !== filters.priceGte ||
      isNewDestination || isNewBounds
    )) {
      setSelectedItems([])
    }
  }, [filters.destinationId, filters.categories, filters.priceLte, filters.priceGte, prevFilters, setSelectedItems, isNewDestination, isNewBounds])

  const handleBoundsChanged = useCallback((newBounds?: MapBounds) => {
    if (!newBounds) {
      return
    }

    onBoundsChanged?.(newBounds)
  }, [onBoundsChanged])

  const onUserMovedMap = useMemo(() => debounce(
    () => {
      if (!map) return
      handleBoundsChanged(getSearchBoundsForMap(map))
    },
    2000,
    { leading: true, trailing: true },
  ), [map, handleBoundsChanged])

  useEffect(() => {
    return () => {
      onUserMovedMap.cancel()
    }
  }, [onUserMovedMap])

  const handleMoveStart = useCallback((event: { originalEvent?: Event }) => {
    hasInteracted.current = event.originalEvent !== undefined
  }, [])

  const handleMoveEnd = useCallback(() => {
    if (!ignoreNextMoveEnd.current && hasInteracted.current) {
      onUserMovedMap()
    }
    ignoreNextMoveEnd.current = false
    hasInteracted.current = false
  }, [onUserMovedMap])

  const activeMarkerId = selectedOfferIds.map(selected => selected.id).join(',')
  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  useEffect(() => {
    const root = rootRef?.current
    if (root) {
      const handleOfferHover = (event: CustomEvent) => {
        const id = event.detail.id
        const offerType = event.detail.type
        const persist = event.detail.persist

        if (!id && persist) return
        if (!id && !persist) {
          setHoveredOffer(undefined)
          return
        }
        setHoveredOffer({ id, type: offerType })
      }

      root.addEventListener('offerHover', handleOfferHover as EventListener)
      return () => {
        root.removeEventListener('offerHover', handleOfferHover as EventListener)
      }
    }
  }, [rootRef])

  return (
    <MapWrapper ref={containerRef}>
      <MapboxProvider useCase={MAPBOX_ACCESS_TOKEN_USE_CASES.experiences}>
        <MapboxMap
          id="le-map"
          initialViewport={{
            latitude: currentMarkers[0]?.latitude ?? 0,
            longitude: currentMarkers[0]?.longitude ?? 0,
            zoom: 3,
          }}
          containerStyle={{
            ...MAP_STYLE,
            visibility: hasInitialCameraApplied ? 'visible' : 'hidden',
          }}
          loadingElement={<LoadingIndicator inline />}
          showNavigationControl={!isMobileSize}
          navigationControlPosition="top-right"
          dragRotate={false}
          touchPitch={false}
          minZoom={3}
          maxZoom={19}
          onMapReady={handleMapReady}
          onClick={onDeselectOffers}
          onMoveStart={handleMoveStart}
          onMoveEnd={handleMoveEnd}
        >
          {markers.map((marker, index) => {
            const isHovered = hoveredOffer?.id ? marker.id.split(',').includes(hoveredOffer.id) : false
            return (
              <SearchMapMarker
                key={marker.id}
                marker={marker}
                onClick={onMarkerClick}
                scaleDown={isMobileSize}
                active={selectedOfferIds.some((offerId) => marker.id.split(',').includes(offerId.id))}
                visited={visitedMarkerIds.has(marker.id)}
                isHovered={isHovered}
                index={index}
              />
            )
          })}
        </MapboxMap>
      </MapboxProvider>
      {fetching && (
        <FloatingBanner>
          <TextButton
            kind="secondary"
            variant="dark"
            nonInteractable={fetching}
            endIcon={<ColourfulLoadingAnimation size={8} />}
          >
            {intl.formatMessage({
              id: 'Imepz7e5m3YifwizOonY',
              defaultMessage: 'Loading experiences',
              description: 'Loading indicator text while experience search results are being fetched on map',
            })}
          </TextButton>
        </FloatingBanner>
      )}
      {!!experiences.length && <ExperienceSearchMapCarousel hidden={hideOfferCarousel} />}
    </MapWrapper>
  )
}

function mapStateToProps(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
  }
}

export default connect(mapStateToProps)(ExperienceSearchMap)
